import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import de from '@/i18n/locales/de/common.json'
import en from '@/i18n/locales/en/common.json'

export const SUPPORTED_LANGUAGES = ['de', 'en'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

/** German is the source language: every key exists here first, the English file follows. */
export const FALLBACK_LANGUAGE: Language = 'de'

const STORAGE_KEY = 'wp-lead.language'

function isSupported(value: string): value is Language {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

/** `de-DE`, `DE`, `de` all become `de`; anything unsupported becomes null. */
export function normalizeLanguage(value: string | null | undefined): Language | null {
  if (!value) {
    return null
  }
  const base = value.toLowerCase().split('-')[0]
  return isSupported(base) ? base : null
}

/**
 * localStorage, not sessionStorage: unlike the tokens this is a preference, not a credential, and
 * a user who picked English once should not have to pick it again in the next tab. Reads can
 * throw when the browser blocks site data, and a missing preference is not an error.
 */
function storedLanguage(): Language | null {
  try {
    return normalizeLanguage(window.localStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

function storeLanguage(language: Language): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, language)
  } catch {
    // A preference that cannot be remembered still applies to this session.
  }
}

function resolveInitialLanguage(): Language {
  const explicit = storedLanguage()
  if (explicit) {
    return explicit
  }
  for (const candidate of navigator.languages ?? [navigator.language]) {
    const language = normalizeLanguage(candidate)
    if (language) {
      return language
    }
  }
  return FALLBACK_LANGUAGE
}

/** An explicit choice by the user. Persisted, and it outranks every hint from elsewhere. */
export function setLanguage(language: Language): void {
  storeLanguage(language)
  void i18n.changeLanguage(language)
}

/**
 * Applies the `locale` from the Keycloak profile. Only a hint: it must never override a choice
 * the user made in the app itself, which is why it stops at a stored preference.
 */
export function applyLanguageHint(hint: string | null | undefined): void {
  if (storedLanguage()) {
    return
  }
  const language = normalizeLanguage(hint)
  if (language && language !== i18n.resolvedLanguage) {
    void i18n.changeLanguage(language)
  }
}

/** What to hand Keycloak as `ui_locales`, so its login screen speaks the same language. */
export function currentLanguage(): Language {
  return normalizeLanguage(i18n.resolvedLanguage) ?? FALLBACK_LANGUAGE
}

void i18n.use(initReactI18next).init({
  resources: {
    de: { common: de },
    en: { common: en },
  },
  ns: ['common'],
  defaultNS: 'common',
  lng: resolveInitialLanguage(),
  fallbackLng: FALLBACK_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  interpolation: {
    // React escapes on render already; escaping here too would double-encode umlauts in
    // interpolated values.
    escapeValue: false,
  },
})

// Screen readers pick their pronunciation from this attribute, so it has to follow the UI.
const syncDocumentLanguage = (language: string) => {
  document.documentElement.lang = normalizeLanguage(language) ?? FALLBACK_LANGUAGE
}
syncDocumentLanguage(i18n.resolvedLanguage ?? FALLBACK_LANGUAGE)
i18n.on('languageChanged', syncDocumentLanguage)

export default i18n
